import { FolderOpenOutlined } from "@ant-design/icons";
import {
  JOURNEY_STEP_INPUT_TYPE_OPTIONS,
  JOURNEY_STEP_MEDIA_TYPE_OPTIONS,
  JourneyStepInputType,
  JourneyStepMediaType,
} from "@model";
import { buildMediaFullUrl } from "@components";
import { Button, Form, Input, Select, Space } from "antd";
import type { FormInstance, FormListFieldData } from "antd/es/form";

type StepListFieldProps = {
  name: number;
  restField: Omit<FormListFieldData, "name" | "key">;
};

interface SelectOption {
  value: string;
  label: string;
}

const mediaPreviewSrc = (path?: string) => {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return buildMediaFullUrl(path);
};

interface MediaTalkStepFieldsProps extends StepListFieldProps {
  form: FormInstance;
  stepPathPrefix: (string | number)[];
  characterOptions: SelectOption[];
  loadingStepOptions: boolean;
  onOpenFileManager: () => void;
}

const MediaTalkStepFields = ({
  name,
  restField,
  form,
  stepPathPrefix,
  characterOptions,
  loadingStepOptions,
  onOpenFileManager,
}: MediaTalkStepFieldsProps) => {
  const mediaMd = Form.useWatch(
    [...stepPathPrefix, "mediaFile", "md"],
    form,
  ) as string | undefined;
  const mediaSm = Form.useWatch(
    [...stepPathPrefix, "mediaFile", "sm"],
    form,
  ) as string | undefined;
  const mediaType = Form.useWatch([...stepPathPrefix, "mediaType"], form) as
    | JourneyStepMediaType
    | undefined;
  const mediaPreview = mediaMd || mediaSm;
  const resolvedMediaType = mediaType ?? JourneyStepMediaType.Image;

  return (
    <>
      <Form.Item
        {...restField}
        name={[name, "mediaType"]}
        label="Media type"
        initialValue={JourneyStepMediaType.Image}
        rules={[{ required: true, message: "Required" }]}
      >
        <Select options={JOURNEY_STEP_MEDIA_TYPE_OPTIONS} />
      </Form.Item>

      <Form.Item name={[name, "mediaFile", "md"]} hidden>
        <Input />
      </Form.Item>
      <Form.Item name={[name, "mediaFile", "sm"]} hidden>
        <Input />
      </Form.Item>

      <Form.Item
        label="Media file"
        required
        tooltip="Pick from file manager. Image for now; voice and video later."
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          {resolvedMediaType === JourneyStepMediaType.Image && (
            <div
              style={{
                width: 120,
                height: 120,
                borderRadius: 8,
                border: "1px dashed #d9d9d9",
                background: "#fafafa",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                flexShrink: 0,
              }}
            >
              {mediaPreview ? (
                <img
                  src={mediaPreviewSrc(mediaPreview)}
                  alt="Step media"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <span style={{ color: "#bfbfbf", fontSize: 12 }}>No file</span>
              )}
            </div>
          )}
          <Space direction="vertical" size={8}>
            <Space wrap>
              <Button icon={<FolderOpenOutlined />} onClick={onOpenFileManager}>
                Select from file manager
              </Button>
              {mediaPreview && (
                <Button
                  danger
                  type="link"
                  onClick={() =>
                    form.setFieldValue(
                      [...stepPathPrefix, "mediaFile"],
                      undefined,
                    )
                  }
                >
                  Clear
                </Button>
              )}
            </Space>
            {resolvedMediaType !== JourneyStepMediaType.Image && (
              <span style={{ color: "#8c8c8c", fontSize: 12 }}>
                {mediaPreview
                  ? `Selected: ${mediaPreview}`
                  : "No file selected yet"}
              </span>
            )}
          </Space>
        </div>
      </Form.Item>

      <Form.Item
        {...restField}
        name={[name, "inputType"]}
        label="Learner input"
        tooltip="How the learner may respond to the character in the app"
        initialValue={JourneyStepInputType.Voice}
        rules={[{ required: true, message: "Required" }]}
      >
        <Select options={JOURNEY_STEP_INPUT_TYPE_OPTIONS} />
      </Form.Item>

      <Form.Item
        {...restField}
        name={[name, "characterId"]}
        label="AI character"
        rules={[{ required: true, message: "Required" }]}
      >
        <Select
          showSearch
          placeholder="Select character"
          options={characterOptions}
          optionFilterProp="label"
          loading={loadingStepOptions}
          disabled={loadingStepOptions}
          notFoundContent="No characters for selected language tab"
        />
      </Form.Item>

      <Form.Item
        {...restField}
        name={[name, "stepCustomInstruction"]}
        label="Step custom instruction"
        tooltip="Tell the AI how to guide the conversation for this step"
        rules={[{ required: true, message: "Required" }]}
      >
        <Input.TextArea
          rows={10}
          placeholder="e.g. A busy street market with fruit vendors and shoppers"
        />
      </Form.Item>

      <Form.Item
        {...restField}
        name={[name, "whenBeDone"]}
        label="When be done"
        tooltip="Tell the AI when this conversation should be considered finished"
        rules={[{ required: true, message: "Required" }]}
      >
        <Input.TextArea
          rows={6}
          placeholder="e.g. After the learner describes at least three things in the photo"
        />
      </Form.Item>
    </>
  );
};

export default MediaTalkStepFields;
